import { useEffect, useRef, useState } from "react";
import { DEVELOPMENTS, devSpecs } from "../lib/developments";
import { Link, useNavigate } from "react-router-dom";
import { CookieConsent } from "../components/CookieConsent";
import { LegalModal } from "../components/LegalModal";
import {
  ArrowRight, ArrowUpRight, Menu, X, Instagram,
  LineChart, Home as HomeIcon, MapPin,
  Cpu, Factory, Leaf, Wifi, Network, Building, Calculator, PencilRuler, Sparkles,
} from "lucide-react";
import "./landing.css";

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/962c596f-d67b-4783-b3e1-b14b5bd763bc/images/0be464cfb95af9a0bb426e99d8af92d42d7d8a206e667485266c4951b6853bc4.jpeg",
};

function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setInView(true); ob.unobserve(el); } }),
      { threshold: 0.15 }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${inView ? "in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</Tag>;
}

const ROUTES = { home: "/", about: "/about", developments: "/developments", approach: "/process", teknology: "/teknology", investment: "/partners" };

const NAV = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Developments", id: "developments" },
  { label: "Our Process", id: "approach" },
  { label: "TEKnology", id: "teknology" },
  { label: "Partners", id: "investment" },
  { label: "Contact", id: "contact" },
];

const TEKNOLOGY = [
  { icon: MapPin, title: "Property Intelligence", body: "Bring property information, zoning, location, infrastructure, and market data together to better understand a site's potential." },
  { icon: Cpu, title: "Feasibility & Development Modeling", body: "Explore development possibilities, evaluate site constraints, and compare potential project outcomes before committing significant resources." },
  { icon: Calculator, title: "Financial Intelligence", body: "Connect acquisition assumptions, development budgets, projected costs, and potential returns to understand the economics behind each opportunity." },
  { icon: PencilRuler, title: "Design & Planning", body: "Bring site planning, development concepts, and design considerations into the decision-making process earlier." },
  { icon: Network, title: "Connected Project Management", body: "Maintain greater visibility into development milestones, documentation, budgets, schedules, and project progress." },
  { icon: Sparkles, title: "Angela AI Intelligence", body: "Our integrated AI intelligence supports research, analysis, and development planning—helping turn complex project information into more actionable insights." },
];

const STEPS = [
  { n: "01", title: "Discover", sub: "See the Opportunity.", body: "Every property has a story. We identify opportunities, explore untapped potential, and consider what a property could become." },
  { n: "02", title: "Analyze", sub: "Understand What's Possible.", body: "We evaluate zoning, utilities, market demand, development feasibility, financial considerations, and potential risks to establish a realistic picture of the opportunity." },
  { n: "03", title: "Acquire", sub: "Create the Right Foundation.", body: "Whether through a traditional acquisition, landowner partnership, or joint venture, we explore the structure that best aligns the property, project, and people involved." },
  { n: "04", title: "Plan", sub: "Build the Roadmap.", body: "We establish the development vision, financial strategy, project budget, schedule, and entitlement pathway to create a coordinated plan for moving forward." },
  { n: "05", title: "Design", sub: "Give the Vision Form.", body: "We translate the opportunity into purposeful architecture and site planning—balancing functionality, market demand, project economics, and the experience of the people who will ultimately use the space." },
  { n: "06", title: "Develop", sub: "Bring It to Life.", body: "Through coordinated planning, approvals, engineering, construction, and development management, we guide the project through the complexities of execution." },
  { n: "07", title: "Deliver", sub: "Realize the Opportunity.", body: "We bring each development toward its intended outcome—whether individual property sales, a completed community, commercial occupancy, or another strategically planned exit." },
];



const PARTNERSHIP_TYPES = ["Landowner", "Investor / Capital Partner", "Broker / Agent", "Development Partner", "Business / Tenant", "Municipality", "Other"];

const FOCUS = [
  { icon: HomeIcon, title: "Residential Development", body: "Custom-designed homes, attached accessory dwelling units, subdivisions, and residential communities." },
  { icon: Building, title: "Commercial Development", body: "We transform land into purposeful commercial destinations—from flex-industrial spaces and business parks to retail, mixed-use, and ground-up developments." },
  { icon: MapPin, title: "Land Development", body: "Land acquisition, entitlements, subdivision, infrastructure, and development-ready parcels." },
];

const PRINCIPLES = [
  { n: "01", title: "Acquire Intelligently", body: "Understand the opportunity, evaluate its risks, and establish an acquisition or partnership structure supported by realistic project economics." },
  { n: "02", title: "Design With Purpose", body: "Create market-driven development concepts that balance functionality, demand, financial feasibility, and long-term relevance." },
  { n: "03", title: "Protect Capital", body: "Establish realistic budgets, identify potential challenges early, and maintain financial discipline throughout the development lifecycle." },
  { n: "04", title: "Create Lasting Value", body: "Align the development strategy with the project's intended outcome, focusing on sound execution and responsible value creation." },
];

const PARTNER_WAYS = [
  { n: "01", label: "Landowner Partnerships", title: "Your Land. Greater Possibilities.", body: ["Owning land doesn't mean you have to develop it alone—or that selling it outright is your only option.", "We work with property owners to explore potential opportunities, including direct acquisitions, land contributions, and joint venture arrangements that may allow owners to participate in the future value of their property."] },
  { n: "02", label: "Investment Partnerships", title: "Capital With a Development Strategy.", body: ["We welcome relationships with investors seeking exposure to thoughtfully evaluated real estate development opportunities.", "Our approach emphasizes disciplined underwriting, realistic project economics, clearly defined development strategies, and alignment around each opportunity's objectives and risks."] },
  { n: "03", label: "Strategic Development Partnerships", title: "The Right Expertise Changes What's Possible.", body: ["Successful projects require collaboration.", "We seek relationships with builders, architects, engineers, commercial operators, brokers, capital partners, and other industry professionals who can contribute expertise, opportunities, resources, or execution capabilities to a shared development vision."] },
];

const HERO_CLIPS = ["/hero.mp4", "/hero2.mp4"];
const FADE_S = 0.9;

function HeroVideos({ poster }) {
  const refs = [useRef(null), useRef(null)];
  const [active, setActive] = useState(0);
  const switching = useRef(false);
  const advance = (from) => {
    if (switching.current) return;
    switching.current = true;
    const to = (from + 1) % HERO_CLIPS.length;
    const next = refs[to].current;
    next.currentTime = 0;
    next.play().catch(() => {});
    setActive(to);
    setTimeout(() => { const prev = refs[from].current; prev.pause(); prev.currentTime = 0; switching.current = false; }, FADE_S * 1000 + 150);
  };
  const onTime = (i) => (e) => {
    const v = e.currentTarget;
    if (i === active && v.duration && v.duration - v.currentTime <= FADE_S) advance(i);
  };
  return HERO_CLIPS.map((src, i) => (
    <video key={src} ref={refs[i]} src={src} poster={i === 0 ? poster : undefined} autoPlay={i === 0} muted playsInline preload="auto"
      onTimeUpdate={onTime(i)} onEnded={() => i === active && advance(i)}
      className="absolute inset-0 h-full w-full object-cover transition-opacity ease-linear"
      style={{ opacity: i === active ? 1 : 0, transitionDuration: `${FADE_S * 1000}ms` }}
      data-testid={`hero-video${i ? `-${i + 1}` : ""}`} />
  ));
}

export default function Landing({ page = "home" }) {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, [page]);
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (ROUTES[id]) navigate(ROUTES[id], { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [solid, setSolid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", company: "", partnership_type: "", message: "" });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactErr, setContactErr] = useState("");
  const [legalDoc, setLegalDoc] = useState(null);

  const submitContact = async (e) => {
    e.preventDefault();
    setContactErr("");
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      setContactErr("Please fill in your name, email, and message.");
      return;
    }
    setContactSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });
      if (!res.ok) throw new Error("bad");
      setContactSent(true);
    } catch {
      setContactErr("Something went wrong. Please try again.");
    } finally {
      setContactSubmitting(false);
    }
  };

  const closeContact = () => {
    setContactOpen(false);
    setTimeout(() => { setContactSent(false); setContactErr(""); setContactForm({ name: "", email: "", phone: "", company: "", partnership_type: "", message: "" }); }, 300);
  };

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide the window scrollbar on the marketing landing (scroll still works).
  useEffect(() => {
    document.documentElement.classList.add("arq-landing");
    document.body.classList.add("arq-landing");
    return () => {
      document.documentElement.classList.remove("arq-landing");
      document.body.classList.remove("arq-landing");
    };
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    if (id === "contact") { setContactOpen(true); return; }
    if (id === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    if (ROUTES[id] === window.location.pathname) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    navigate(ROUTES[id]);
  };

  const Btn = ({ variant = "primary", children, ...p }) => (
    <button
      {...p}
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors duration-300 ${
        variant === "primary"
          ? "bg-[#27BEAA]/10 border border-[#27BEAA]/60 text-[#27BEAA] hover:bg-[#27BEAA]/20 backdrop-blur-sm"
          : "border border-white/20 text-white hover:bg-white/5"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="arq-root" id="top" data-testid="landing-page">
      <CookieConsent />
      {/* NAV */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${solid || page !== "home" ? "bg-[#08100E]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <button onClick={() => go("home")} className="flex items-center" data-testid="landing-logo" aria-label="ARQITEK — home">
            <img src="/logo.png" alt="ARQITEK" className="h-6 md:h-7 w-auto" />
          </button>
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              n.href ? (
                <a key={n.id} href={n.href} target="_blank" rel="noopener noreferrer" className="text-[13px] tracking-wide text-white/90 hover:text-white transition-colors" data-testid={`landing-nav-${n.id}`}>
                  {n.label}
                </a>
              ) : (
                <button key={n.id} onClick={() => go(n.id)} className={`text-[13px] tracking-wide transition-colors ${page === n.id ? "text-[#27BEAA]" : "text-white/90 hover:text-white"}`} data-testid={`landing-nav-${n.id}`}>
                  {n.label}
                </button>
              )
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-5">
            <button onClick={() => navigate("/login")} className="text-[13px] tracking-wide text-white/90 hover:text-white transition-colors" data-testid="landing-login-link">
              Login
            </button>
          </div>
          <button className="lg:hidden text-white" onClick={() => setMenuOpen((v) => !v)} data-testid="landing-menu-toggle" aria-label="Menu">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-[#08100E]/95 backdrop-blur-xl border-t border-white/5 px-6 py-6 space-y-4" data-testid="landing-mobile-menu">
            {NAV.map((n) => (
              n.href ? (
                <a key={n.id} href={n.href} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} className="block text-left text-base text-[#AFBCB8] hover:text-white w-full" data-testid={`landing-nav-mobile-${n.id}`}>{n.label}</a>
              ) : (
                <button key={n.id} onClick={() => go(n.id)} className={`block text-left text-base w-full ${page === n.id ? "text-[#27BEAA]" : "text-[#AFBCB8] hover:text-white"}`} data-testid={`landing-nav-mobile-${n.id}`}>{n.label}</button>
              )
            ))}
            <button onClick={() => navigate("/login")} className="block text-left text-base text-[#AFBCB8] hover:text-white w-full">Login</button>
          </div>
        )}
      </header>

      {page !== "home" && <div className="h-20" />}
      {/* HERO */}
      {page === "home" && (<section className="relative h-screen min-h-[640px] w-full flex items-end">
        <HeroVideos poster="/images/hero-first-frame.jpg" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08100E] via-[#08100E]/40 to-[#08100E]/30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pb-20 md:pb-28">
          <div className="max-w-3xl">
            <div className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#27BEAA] mb-6 reveal in">Real Estate Development</div>
            <h1 className="font-clash text-4xl md:text-6xl lg:text-[4.25rem] font-semibold leading-[1.0] tracking-tight">
              Design.<br />Develop.<br />Build.
            </h1>
            <p className="mt-7 text-lg md:text-xl text-[#C6D2CF] max-w-2xl leading-relaxed">
              We transform land into thoughtfully designed developments that create lasting value for people, businesses, and communities.
            </p>
          </div>
        </div>
      </section>)}

      {/* ABOUT */}
      {page === "about" && (<>
      <section id="about" className="border-y border-white/[0.06] arq-grid">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-40 text-center">
          <Reveal>
            <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-5">About ARQITEK</div>
            <h2 className="font-clash text-4xl md:text-5xl font-semibold tracking-tight leading-tight">Everything Created Starts With an ARQITEK.</h2>
            <p className="mt-8 font-clash text-xl md:text-2xl text-white">Before anything exists, it begins with a vision.</p>
            <div className="mt-8 space-y-6 text-[#A5B2AE] text-base md:text-lg leading-relaxed" data-testid="about-body">
              <p>Every building, every community, every great creation begins with someone who sees what others have yet to imagine. Someone who recognizes potential, brings ideas together, and designs a path from possibility to reality.</p>
              <p className="text-white">That belief is the foundation of ARQITEK.</p>
              <p>To us, an architect is more than someone who designs buildings. An architect is a visionary—a creator who sees beyond what exists today and imagines what could exist tomorrow.</p>
              <p>We bring that philosophy to real estate development, where landowners, investors, designers, and development partners come together to transform opportunity into something meaningful.</p>
              <p>Our goal isn't simply to build more. It's to design better opportunities, build stronger partnerships, and create developments that stand the test of time.</p>
            </div>
            <p className="mt-10 font-clash text-xl md:text-2xl text-white">Because everything created starts with an ARQITEK.</p>
          </Reveal>
        </div>
      </section>

      </>)}
      {/* DEVELOPMENTS */}
      {page === "developments" && (
      <section id="developments" className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <Reveal><h2 className="font-clash text-4xl md:text-6xl font-semibold tracking-tight">Latest Developments</h2></Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {DEVELOPMENTS.map((d, i) => (
              <Reveal key={d.name} delay={i * 100}>
                <Link to={`/developments/${d.slug}`} className="arq-card group block" data-testid={`development-card-${i}`}>
                  <div className="relative overflow-hidden rounded-xl border border-white/[0.06]">
                    <img src={d.img} alt={d.name} className="arq-card-img w-full aspect-[16/10] object-cover" />
                  </div>
                  <div className="mt-5">
                    <div className="font-mono2 text-[11px] uppercase tracking-widest text-[#8AA09A]">{d.city} · {d.type}</div>
                    <h3 className="font-clash text-2xl font-medium mt-1.5">{d.name}</h3>
                    <div className={`mt-5 grid gap-3 border-t border-white/[0.06] pt-4 font-mono2 ${d.features ? "grid-cols-3 lg:flex lg:gap-10" : d.specs?.length === 5 ? "grid-cols-3 gap-y-4 sm:grid-cols-5" : "grid-cols-4"}`}>
                      {devSpecs(d).filter(([k]) => k !== "Configuration").map(([k, v]) => (
                        <div key={k} className={d.features ? "lg:whitespace-nowrap" : ""}>
                          <div className="text-[10px] uppercase tracking-widest text-[#66736F]">{k}</div>
                          <div className="text-sm text-white mt-1">{v}</div>
                        </div>
                      ))}
                    </div>
                    {d.features && <div className="mt-5 border-t border-white/[0.06] pt-4 text-sm text-[#8AA09A]" data-testid={`development-features-${i}`}>{d.features}</div>}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      )}
      {/* FOCUS */}
      {false && (
      <section className="border-t border-white/[0.06] bg-[#0B1211]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <Reveal><h2 className="font-clash text-4xl md:text-6xl font-semibold tracking-tight">Development With Purpose.</h2></Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
            {FOCUS.map((f, i) => (
              <Reveal key={f.title} delay={i * 90}>
                <div className="h-full bg-[#0B1211] p-8 hover:bg-[#121A18] transition-colors duration-500">
                  <f.icon className="h-6 w-6 text-[#27BEAA]" strokeWidth={1.5} />
                  <h3 className="font-clash text-xl font-medium mt-6">{f.title}</h3>
                  <p className="mt-3 text-sm text-[#8AA09A] leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      )}
      {/* APPROACH */}
      {page === "approach" && (
      <section id="approach" className="border-y border-white/[0.06] arq-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <Reveal>
            <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-4">The ARQITEK Approach</div>
            <h2 className="font-clash text-4xl md:text-6xl font-semibold tracking-tight">From Potential to Purpose.</h2>
            <p className="mt-6 max-w-3xl text-[#8AA09A] text-base md:text-lg leading-relaxed">
              Great developments don't happen by chance. They begin with recognizing an opportunity, understanding its possibilities, bringing the right people together, and creating a clear path forward.
            </p>
            <p className="mt-4 max-w-3xl text-[#8AA09A] text-base md:text-lg leading-relaxed">
              At ARQITEK, we approach every opportunity with intention—connecting real estate intelligence, thoughtful design, financial discipline, TEKnology, and development expertise to guide projects from initial vision to their intended outcome.
            </p>
          </Reveal>
          <div className="mt-16 relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/10 hidden md:block" />
            <div className="space-y-10 md:space-y-14">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 60}>
                  <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-12 items-start">
                    <div className="flex items-center gap-4">
                      <span className="hidden md:block h-4 w-4 rounded-full border-2 border-[#27BEAA] bg-[#08100E] shrink-0" />
                      <span className="font-mono2 text-[#27BEAA] text-sm">{s.n}</span>
                    </div>
                    <div className="md:grid md:grid-cols-[220px_1fr] md:gap-12 border-b border-white/[0.06] pb-8">
                      <h3 className="font-clash text-2xl md:text-3xl font-medium">{s.title}</h3>
                      <div className="mt-2 md:mt-1 max-w-xl">
                        <p className="text-base md:text-lg text-white">{s.sub}</p>
                        <p className="mt-2 text-sm md:text-base text-[#8AA09A] leading-relaxed">{s.body}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal><p className="mt-16 md:mt-20 font-clash text-2xl md:text-3xl text-white" data-testid="process-closing">One Vision. A Connected Approach. A Purposeful Outcome.</p></Reveal>
        </div>
      </section>

      )}
      {/* TEKNOLOGY */}
      {page === "teknology" && (<>
      <section id="teknology" className="border-t border-white/[0.06] bg-[#0B1211]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-stretch">
            <Reveal>
              <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-4">TEKnology</div>
              <h2 className="font-clash text-4xl md:text-6xl font-semibold tracking-tight leading-tight">Better Decisions Before We Build.</h2>
              <div className="mt-6 space-y-5 text-[#8AA09A] text-base md:text-lg leading-relaxed" data-testid="teknology-intro">
                <p>Great developments begin with informed decisions. Yet real estate development often involves fragmented information, disconnected systems, and critical decisions made without a complete picture.</p>
                <p className="font-clash text-xl md:text-2xl text-white">ARQITEK is changing that.</p>
                <p>We've created a connected development intelligence platform that brings property research, feasibility, financial modeling, design, and project management into one environment—helping our team evaluate opportunities, understand risks, compare possibilities, and make more informed decisions throughout the development lifecycle.</p>
                <p className="text-white">Our TEKnology doesn't replace experience or human judgment. It strengthens both.</p>
              </div>
            </Reveal>
            <Reveal delay={120} className="h-full">
              <div className="relative h-[320px] sm:h-[420px] lg:h-full rounded-xl overflow-hidden border border-white/[0.06]">
                <video src="/intelligence.mp4" poster="/images/intelligence-poster.jpg" autoPlay muted loop playsInline preload="auto" className="absolute inset-0 w-full h-full object-cover" data-testid="intelligence-video" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DEVELOPMENT INTELLIGENCE */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <Reveal>
            <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-4">Development Intelligence</div>
            <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-4xl">One Connected Platform. Greater Clarity at Every Stage.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEKNOLOGY.map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="h-full bg-[#0F1513] border border-white/[0.06] rounded-xl p-8 hover:border-[#27BEAA]/40 transition-colors duration-500" data-testid={`teknology-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <c.icon className="h-6 w-6 text-[#27BEAA]" strokeWidth={1.5} />
                    <span className="font-mono2 text-xs text-[#27BEAA]/70">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h4 className="font-clash text-xl font-medium mt-6">{c.title}</h4>
                  <p className="mt-3 text-sm text-[#8AA09A] leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 md:mt-24 border-t border-white/[0.06] pt-12 max-w-3xl" data-testid="arqitek-difference">
              <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-4">The ARQITEK Difference</div>
              <h3 className="font-clash text-3xl md:text-4xl font-semibold tracking-tight">Technology With a Purpose.</h3>
              <p className="mt-6 text-[#A5B2AE] text-base md:text-lg leading-relaxed">We don't believe innovation is about adding more software to an already complicated process.</p>
              <p className="mt-4 text-white text-base md:text-lg leading-relaxed">It's about connecting the information, people, and decisions that move a development forward.</p>
              <p className="mt-4 text-[#A5B2AE] text-base md:text-lg leading-relaxed">By combining real estate expertise with proprietary TEKnology, ARQITEK is building a more connected approach to identifying opportunities, evaluating risk, planning projects, and managing development.</p>
              <p className="mt-8 text-[#27BEAA] font-clash text-xl md:text-2xl">Real Estate Intelligence. Designed Into Every Decision.</p>
            </div>
          </Reveal>
        <Reveal>
          <div className="mt-16 md:mt-20 border-t border-white/[0.06] pt-12 max-w-3xl" data-testid="partner-block">
            <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-4">Partner With ARQITEK</div>
            <h3 className="font-clash text-2xl md:text-3xl font-medium leading-snug">Your Property. Our Development Expertise. Shared Potential.</h3>
            <p className="mt-5 text-[#A5B2AE] text-base md:text-lg leading-relaxed">Owning land doesn't mean you have to develop it alone. ARQITEK explores creative partnerships that can bring together land, capital, planning, design, and development expertise to unlock a property's potential.</p>
            <p className="mt-5 text-[#27BEAA] font-clash text-lg">Together, we evaluate the opportunity and structure a path forward that aligns with each partner's goals.</p>
          </div>
        </Reveal>
      </section>

      </>)}
      {/* INVESTMENT */}
      {page === "investment" && (
      <section id="investment" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
        <Reveal>
          <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-4">Partnerships</div>
          <h2 className="font-clash text-4xl md:text-6xl font-semibold tracking-tight leading-tight">Different Contributions. Shared Opportunity.</h2>
          <p className="mt-8 font-clash text-xl md:text-2xl text-white max-w-3xl">Great developments begin when the right opportunities meet the right people.</p>
          <div className="mt-6 max-w-3xl space-y-5 text-[#8AA09A] text-base md:text-lg leading-relaxed">
            <p>At ARQITEK, we believe real estate development creates possibilities for more than just the developer. Landowners, investors, and strategic partners each bring something valuable—and the right partnership can unlock opportunities that might otherwise remain unrealized.</p>
            <p>We explore creative development structures that align interests, recognize each partner's contribution, and establish a shared vision for what a property can become.</p>
          </div>
        </Reveal>

        <Reveal><h3 className="font-clash text-2xl md:text-3xl font-medium mt-20 md:mt-28">Ways to Partner With ARQITEK</h3></Reveal>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {PARTNER_WAYS.map((w, i) => (
            <Reveal key={w.n} delay={i * 90} className="h-full">
              <div className="h-full bg-[#0F1513] border border-white/[0.06] rounded-xl p-8 hover:border-[#27BEAA]/40 transition-colors duration-500" data-testid={`partner-way-${i}`}>
                <div className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#27BEAA]">{w.n} — {w.label}</div>
                <h4 className="font-clash text-xl md:text-2xl font-medium mt-5 leading-snug">{w.title}</h4>
                {w.body.map((t) => <p key={t} className="mt-4 text-sm md:text-base text-[#8AA09A] leading-relaxed">{t}</p>)}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-20 md:mt-28 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-4">Our Investment Philosophy</div>
          <h3 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight">We Create Value Before We Build.</h3>
          <p className="mt-6 max-w-3xl text-[#8AA09A] text-base md:text-lg leading-relaxed">Our approach is grounded in four principles that guide how we evaluate opportunities, structure projects, and manage development capital.</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06]">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <div className="h-full bg-[#08100E] p-8">
                <div className="font-mono2 text-[#27BEAA] text-sm">{p.n}</div>
                <h3 className="font-clash text-xl font-medium mt-6">{p.title}</h3>
                <p className="mt-3 text-sm text-[#8AA09A] leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-20 md:mt-28 border-t border-white/[0.06] pt-16 text-center" data-testid="investment-cta">
            <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-5">Let's Explore What's Possible.</div>
            <p className="font-clash text-2xl md:text-4xl font-semibold tracking-tight">Have land, capital, expertise, or a development opportunity?</p>
            <p className="mt-4 text-[#8AA09A] text-base md:text-lg">We'd welcome the conversation.</p>
            <div className="mt-9 flex justify-center">
              <Btn onClick={() => setContactOpen(true)} data-testid="investment-partner-cta">Partner With ARQITEK <ArrowRight className="h-4 w-4" /></Btn>
            </div>
          </div>
        </Reveal>
      </section>

      )}
      {/* FINAL CTA */}
      {page !== "investment" && (<section id="contact" className="border-t border-white/[0.06] max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 text-center">
        <Reveal>
          <h2 className="font-clash text-4xl md:text-6xl font-semibold tracking-tight">The Next Development Starts Here.</h2>
          <p className="mt-6 text-[#8AA09A] text-base md:text-lg max-w-2xl mx-auto">Partner with ARQITEK to unlock real estate opportunities through intelligent acquisition, thoughtful planning, and disciplined development.</p>
          <div className="mt-9 flex flex-wrap gap-4 justify-center">
            <Btn onClick={() => setContactOpen(true)} data-testid="contact-open-cta">Explore Partnerships <ArrowRight className="h-4 w-4" /></Btn>
            
          </div>
        </Reveal>
      </section>)}

      {/* FOOTER */}
      <footer className="border-t border-white/[0.06]" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
            {/* Brand */}
            <div className="md:col-span-5">
              <img src="/logo.png" alt="ARQITEK" className="h-6 md:h-7 w-auto" />
              <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#8AA09A]">
                Residential, commercial, and land developments where real estate, design, and TEKnology converge.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <a href="https://www.instagram.com/arqitek.co?igsi=NTc4MTIwNjQ2YQ==" target="_blank" rel="noreferrer" aria-label="Instagram" className="h-10 w-10 rounded-full border border-white/12 flex items-center justify-center text-[#8AA09A] hover:text-white hover:border-[#27BEAA]/50 transition-colors" data-testid="footer-instagram"><Instagram className="h-4 w-4" /></a>
              </div>
            </div>

            {/* Explore */}
            <div className="md:col-span-2">
              <div className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 mb-5">Explore</div>
              <ul className="space-y-3.5">
                <li><button onClick={() => go("developments")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-developments">Developments</button></li>
                <li><button onClick={() => go("approach")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-process">Our Process</button></li>
                <li><button onClick={() => go("teknology")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-teknology">TEKnology</button></li>
                <li><button onClick={() => go("investment")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-investment">Partners</button></li>
              </ul>
            </div>

            {/* Company */}
            <div className="md:col-span-2">
              <div className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 mb-5">Company</div>
              <ul className="space-y-3.5">
                <li><button onClick={() => go("about")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-about">About</button></li>
                <li><button onClick={() => setContactOpen(true)} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-contact">Contact</button></li>
                <li><a href="https://arqitrak.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-arqitrak">Arqitrak</a></li>
                <li><button onClick={() => navigate("/login")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-login-link">Login</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div className="md:col-span-3">
              <div className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 mb-5">Legal</div>
              <ul className="space-y-3.5">
                <li><button onClick={() => setLegalDoc("privacy")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-privacy">Privacy Policy</button></li>
                <li><button onClick={() => setLegalDoc("terms")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-terms">Terms of Service</button></li>
                <li><button onClick={() => setLegalDoc("cookies")} className="text-sm text-[#8AA09A] hover:text-white transition-colors" data-testid="footer-link-cookies">Cookie Policy</button></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-16 border-t border-white/[0.06] pt-8 flex flex-col md:flex-row items-center justify-between gap-5">
            <div className="font-mono2 text-[11px] uppercase tracking-widest text-[#66736F]" data-testid="footer-copyright">© {new Date().getFullYear()} ARQITEK LLC. All rights reserved.</div>
            <div className="flex items-center gap-4 text-[13px] text-[#66736F]">
              <button onClick={() => setLegalDoc("privacy")} className="hover:text-white transition-colors" data-testid="footer-bottom-privacy">Privacy</button>
              <span className="text-white/15">|</span>
              <button onClick={() => setLegalDoc("terms")} className="hover:text-white transition-colors" data-testid="footer-bottom-terms">Terms</button>
              <span className="text-white/15">|</span>
              <button onClick={() => setLegalDoc("cookies")} className="hover:text-white transition-colors" data-testid="footer-bottom-cookies">Cookies</button>
            </div>
          </div>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      {legalDoc && <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />}

      {/* CONTACT MODAL */}
      {contactOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="contact-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeContact} />
          <div className="relative w-full max-w-lg bg-[#0B1211] border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl">
            <button onClick={closeContact} aria-label="Close" data-testid="contact-modal-close" className="absolute top-5 right-5 text-[#8AA09A] hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
            {contactSent ? (
              <div className="text-center py-6" data-testid="contact-success">
                <div className="mx-auto h-14 w-14 rounded-full border border-[#27BEAA]/50 bg-[#27BEAA]/10 flex items-center justify-center mb-6">
                  <ArrowRight className="h-6 w-6 text-[#27BEAA]" />
                </div>
                <h3 className="font-clash text-2xl font-medium text-white">Thank you.</h3>
                <p className="mt-3 text-[#8AA09A] text-sm leading-relaxed">Your message has been received. The ARQITEK team will be in touch shortly.</p>
                <button onClick={closeContact} className="mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium bg-[#27BEAA]/10 border border-[#27BEAA]/60 text-[#27BEAA] hover:bg-[#27BEAA]/20 transition-colors" data-testid="contact-done-btn">Done</button>
              </div>
            ) : (
              <>
                <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#27BEAA] mb-3">Get in touch</div>
                <h3 className="font-clash text-2xl md:text-3xl font-medium text-white flex items-center gap-2.5 flex-wrap">Contact <img src="/logo.png" alt="ARQITEK" className="h-6 md:h-7 w-auto object-contain" /></h3>
                <p className="mt-2 text-[#8AA09A] text-sm">Tell us about your project or opportunity.</p>
                <form onSubmit={submitContact} className="mt-6 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name *" data-testid="contact-input-name" className="w-full bg-[#0F1513] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-[#66736F] focus:border-[#27BEAA]/60 focus:outline-none transition-colors" />
                    <input value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email *" type="email" data-testid="contact-input-email" className="w-full bg-[#0F1513] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-[#66736F] focus:border-[#27BEAA]/60 focus:outline-none transition-colors" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Phone" data-testid="contact-input-phone" className="w-full bg-[#0F1513] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-[#66736F] focus:border-[#27BEAA]/60 focus:outline-none transition-colors" />
                    <input value={contactForm.company} onChange={(e) => setContactForm((f) => ({ ...f, company: e.target.value }))} placeholder="Company" data-testid="contact-input-company" className="w-full bg-[#0F1513] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-[#66736F] focus:border-[#27BEAA]/60 focus:outline-none transition-colors" />
                  </div>
                  <div data-testid="contact-partnership-type">
                    <div className="text-xs text-[#8AA09A] mb-2">I am a…</div>
                    <div className="flex flex-wrap gap-2">
                      {PARTNERSHIP_TYPES.map((t) => {
                        const on = contactForm.partnership_type === t;
                        return (
                          <button type="button" key={t} onClick={() => setContactForm((f) => ({ ...f, partnership_type: on ? "" : t }))} aria-pressed={on}
                            data-testid={`contact-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${on ? "border-[#27BEAA]/70 bg-[#27BEAA]/15 text-[#27BEAA]" : "border-white/10 text-[#A5B2AE] hover:border-white/25 hover:text-white"}`}>{t}</button>
                        );
                      })}
                    </div>
                  </div>
                  <textarea value={contactForm.message} onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))} placeholder="How can we help? *" rows={4} data-testid="contact-input-message" className="w-full bg-[#0F1513] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-[#66736F] focus:border-[#27BEAA]/60 focus:outline-none transition-colors resize-none" />
                  {contactErr && <p className="text-sm text-red-400" data-testid="contact-error">{contactErr}</p>}
                  <button type="submit" disabled={contactSubmitting} data-testid="contact-submit-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium bg-[#27BEAA]/10 border border-[#27BEAA]/60 text-[#27BEAA] hover:bg-[#27BEAA]/20 transition-colors disabled:opacity-50">
                    {contactSubmitting ? "Sending…" : "Send Message"} {!contactSubmitting && <ArrowRight className="h-4 w-4" />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
